// TypeScript mirror of the C# ProductDataFilenameConvention value shape.
// Kept flat with nested Filename + WriteReason sub-shapes to match the
// backend JSON exactly — see docs/adr/004-product-data-file-formats/README.md
// § "The target shape".

export type FilenameIdentifier = 'Barcode' | 'Reference' | 'Description';

export const ALL_FILENAME_IDENTIFIERS: readonly FilenameIdentifier[] = [
  'Barcode',
  'Reference',
  'Description',
] as const;

export type FileExtension = 'Json' | 'Csv';

export const ALL_FILE_EXTENSIONS: readonly FileExtension[] = ['Json', 'Csv'] as const;

const FILENAME_IDENTIFIER_LABEL: Record<FilenameIdentifier, string> = {
  Barcode: 'Barcode',
  Reference: 'Reference',
  Description: 'Description',
};

export const filenameIdentifierLabel = (id: FilenameIdentifier): string =>
  FILENAME_IDENTIFIER_LABEL[id];

const FILE_EXTENSION_LABEL: Record<FileExtension, string> = {
  Json: 'JSON',
  Csv: 'CSV',
};

export const fileExtensionLabel = (ext: FileExtension): string => FILE_EXTENSION_LABEL[ext];

export interface ProductDataFilenameConventionFilename {
  identifier: FilenameIdentifier;
  extensions: FileExtension[];
}

// Polymorphic WriteReason discriminated by `kind` — matches the C#
// [JsonPolymorphic(TypeDiscriminatorPropertyName = "kind")] shape.

export type WriteReasonKind = 'initialSetup' | 'conventionChange' | 'correction' | 'other';

export interface WriteReasonInitialSetup {
  kind: 'initialSetup';
}

export interface WriteReasonConventionChange {
  kind: 'conventionChange';
}

export interface WriteReasonCorrection {
  kind: 'correction';
}

export interface WriteReasonOther {
  kind: 'other';
  detail: string;
}

export type ProductDataFilenameConventionWriteReason =
  | WriteReasonInitialSetup
  | WriteReasonConventionChange
  | WriteReasonCorrection
  | WriteReasonOther;

// Reasons user can pick in the Update dialog — InitialSetup is server-controlled.
export type UpdateWriteReasonKind = 'conventionChange' | 'correction' | 'other';

export const USER_SELECTABLE_WRITE_REASON_KINDS: readonly UpdateWriteReasonKind[] = [
  'conventionChange',
  'correction',
  'other',
] as const;

const WRITE_REASON_LABEL: Record<WriteReasonKind, string> = {
  initialSetup: 'Initial setup',
  conventionChange: 'Convention change',
  correction: 'Correction',
  other: 'Other',
};

export const writeReasonLabel = (kind: WriteReasonKind): string => WRITE_REASON_LABEL[kind];

// Client-side regex mirrors the FluentValidator on the backend
// (CreateProductDataFilenameConvention.CommandValidator.OtherDetailPattern):
// requires ≥3 alphabetic characters — whitespace/punctuation allowed.
export const OTHER_DETAIL_PATTERN = /^(?=(?:.*[a-zA-Z]){3,}).+$/;

export interface ProductDataFilenameConvention {
  id: string;
  organizationId: string;
  createdAt: string;
  filename: ProductDataFilenameConventionFilename;
  writeReason: ProductDataFilenameConventionWriteReason;
  // Optional user-attribution field — populated by the backend on the list
  // endpoint when the auditing user can be resolved. Falls back to "Unknown"
  // in the history view when absent.
  createdByEmail?: string;
}

export interface ProductDataFilenameConventionCreatePayload {
  filename: ProductDataFilenameConventionFilename;
  writeReason: ProductDataFilenameConventionWriteReason;
}

export const DEFAULT_FILENAME: ProductDataFilenameConventionFilename = {
  identifier: 'Barcode',
  extensions: ['Json'],
};
